import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.digitalxtension.com';

const client = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ── Attach JWT + active account context to every request ────
client.interceptors.request.use((config) => {
  const token = localStorage.getItem('dx_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // Attach x-account-id header when operating in a switched account context
  const activeAccount = localStorage.getItem('dx_active_account');
  if (activeAccount) config.headers['x-account-id'] = activeAccount;

  return config;
});

// ── Handle 401 globally ──────────────────────────────────────
client.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('dx_token');
      localStorage.removeItem('dx_user');
      localStorage.removeItem('dx_active_account');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ── Auth ─────────────────────────────────────────────────────
export const login  = (email, password) => client.post('/api/auth/login', { email, password });
export const getMe  = ()                => client.get('/api/auth/me');
export const logout = ()                => client.post('/api/auth/logout');

// ── Accounts ─────────────────────────────────────────────────
export const getAccounts      = ()       => client.get('/api/accounts');
export const getAccount       = (id)     => client.get(`/api/accounts/${id}`);
export const createAccount    = (data)   => client.post('/api/accounts', data);
export const updateAccount    = (id, data) => client.patch(`/api/accounts/${id}`, data);
export const suspendAccount   = (id)     => client.post(`/api/accounts/${id}/suspend`);
export const activateAccount  = (id)     => client.post(`/api/accounts/${id}/activate`);
export const getAccountUsers  = (id)     => client.get(`/api/accounts/${id}/users`);
export const getPlans         = ()       => client.get('/api/accounts/plans/all');
export const createPlan       = (data)   => client.post('/api/accounts/plans', data);
export const updatePlan       = (id, data) => client.patch(`/api/accounts/plans/${id}`, data);
export const deletePlan       = (id)     => client.delete(`/api/accounts/plans/${id}`);

// ── Leads ─────────────────────────────────────────────────────
export const getLeads        = (params)    => client.get('/api/leads', { params });
export const getLead         = (id)        => client.get(`/api/leads/${id}`);
export const createLead      = (data)      => client.post('/api/leads', data);
export const updateLead      = (id, data)  => client.patch(`/api/leads/${id}`, data);
export const deleteLead      = (id)        => client.delete(`/api/leads/${id}`);
export const bulkUpsertLeads = (leads)     => client.post('/api/leads/bulk-upsert', { leads });
export const addNote         = (id, body)  => client.post(`/api/leads/${id}/note`, { body });

// ── Users ─────────────────────────────────────────────────────
export const getUsers   = ()           => client.get('/api/users');
export const updateUser = (id, data)   => client.patch(`/api/users/${id}`, data);
export const deleteUser = (id)         => client.delete(`/api/users/${id}`);

// ── Teams ─────────────────────────────────────────────────────
export const getTeams    = ()       => client.get('/api/users/teams/all');
export const createTeam  = (data)   => client.post('/api/users/teams', data);
export const deleteTeam  = (id)     => client.delete(`/api/users/teams/${id}`);

// ── Email ─────────────────────────────────────────────────────
export const testSmtp      = ()     => client.post('/api/email/test');
export const sendEmail     = (data) => client.post('/api/email/send', data);
export const getCampaigns  = ()     => client.get('/api/email/campaigns');
export const getTemplates  = ()     => client.get('/api/email/templates');

// ── AI ────────────────────────────────────────────────────────
export const scoreLead           = (leadId)          => client.post(`/api/ai/score/${leadId}`);
export const draftEmail          = (lead_id, purpose) => client.post('/api/ai/draft-email', { lead_id, purpose });
export const getInsights         = ()                => client.get('/api/ai/insights');
export const getWorkflowSuggestion = (lead_id)       => client.post('/api/ai/workflow-suggestion', { lead_id });

export default client;
